import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Square, Package, Truck, Grid, TrendingUp } from 'lucide-react';
import { toast } from 'react-toastify';
import { CardStats, ConfirmModal } from '@components/ui';
import MaterialPageLayout from '../../components/MaterialPageLayout';
import MaterialDetailModal from '../../components/MaterialDetailModal';
import StokSekme from '../../components/StokSekme';
import StokTablosu from '../../components/StokTablosu';
import StokAktarModal from '../../components/StokAktarModal';
import { formatCurrency } from '../../components/StockIndicator';
import AlciSivaTable from './AlciSivaTable';
import AlciSivaModal from './AlciSivaModal';
import { alciSivaKategorileri, getAlciSivaByInsaat } from '@mocks/materials/alciSiva';
import { getStokHavuzu } from '@mocks/materials/stokHavuzu';
import { getHareketler, hareketTipleri } from '@mocks/materials/stokHareketleri';
import OdemePlanMiniModal from '@components/shared/OdemePlanMiniModal';
import { addMockCariHareket } from '@mocks/cariHesaplar';

export default function AlciSiva() {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);
    const [activeTab, setActiveTab] = useState('kullanim');
    const [filterKategori, setFilterKategori] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [isAktarOpen, setIsAktarOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isOdemePlanOpen, setIsOdemePlanOpen] = useState(false);
    const [odemePlanData, setOdemePlanData] = useState(null);

    const insaatId = showAllConstructions ? null : selectedConstruction?.id;

    const filteredData = useMemo(() => {
        let data = getAlciSivaByInsaat(insaatId);
        if (filterKategori) data = data.filter(i => i.kategori === filterKategori);
        return data;
    }, [insaatId, filterKategori]);

    const stats = useMemo(() => {
        const toplamKalem = filteredData.length;
        const teslimEdilen = filteredData.filter(s => s.durum === 'teslim').length;
        const toplamMaliyet = filteredData.reduce((acc, s) => acc + s.toplamFiyat, 0);
        const toplamMetrekare = filteredData.reduce((acc, s) => acc + s.uygulamaMetrekare, 0);
        return { toplamKalem, teslimEdilen, toplamMaliyet, toplamMetrekare };
    }, [filteredData]);

    const stokHavuzuData = getStokHavuzu('alciSiva');
    const hareketlerData = getHareketler('alciSiva');

    const handleAdd = () => { setSelectedItem(null); setIsModalOpen(true); };
    const handleView = (item) => { setSelectedItem(item); setIsDetailOpen(true); };
    const handleEdit = (item) => { setSelectedItem(item); setIsModalOpen(true); };
    const handleDelete = (item) => { setSelectedItem(item); setIsDeleteOpen(true); };

    const handleSubmit = async (fd, isEdit) => {
        setIsLoading(true);
        await new Promise(r => setTimeout(r, 800));
        if (!isEdit && fd.supplierId && fd.kdvDahilToplam > 0) {
            addMockCariHareket(fd.supplierId, insaatId || 'c1', {
                type: 'purchase', subtype: 'fatura', amount: fd.kdvDahilToplam,
                date: fd.siparisTarihi || new Date().toISOString().split('T')[0],
                description: fd.autoDescription || `Alçı/Sıva — ${fd.miktar} ${fd.birim}`,
            });
        }
        toast.success(isEdit ? 'Malzeme güncellendi' : 'Yeni malzeme eklendi');
        setIsLoading(false); setIsModalOpen(false);
        if (!isEdit && fd.supplierId && fd.kdvDahilToplam > 0) {
            setOdemePlanData({ amount: fd.kdvDahilToplam, companyName: fd.tedarikci });
            setIsOdemePlanOpen(true);
        }
    };
    const handleConfirmDelete = async () => { setIsLoading(true); await new Promise(r => setTimeout(r, 500)); toast.success('Malzeme silindi'); setIsLoading(false); setIsDeleteOpen(false); };
    const handleAktarim = async (d) => { setIsLoading(true); await new Promise(r => setTimeout(r, 500)); toast.success(`${d.miktar} stok aktarıldı`); setIsLoading(false); setIsAktarOpen(false); };
    const handleOdemePlanConfirm = ({ nextDueDate }) => { toast.success(`Ödeme planı oluşturuldu — Vade: ${nextDueDate}`); setIsOdemePlanOpen(false); setOdemePlanData(null); };

    const statsCards = [
        { title: 'Toplam Kalem', value: `${stats.toplamKalem}`, icon: Package, color: 'primary' },
        { title: 'Teslim Edilen', value: `${stats.teslimEdilen}`, icon: Truck, color: 'success' },
        { title: 'Uygulanan', value: `${stats.toplamMetrekare} m²`, icon: Grid, color: 'info' },
        { title: 'Toplam Maliyet', value: formatCurrency(stats.toplamMaliyet), icon: TrendingUp, color: 'warning' },
    ];

    const detailFields = [
        { key: 'siparisTarihi', label: 'Sipariş Tarihi', type: 'date' },
        { key: 'kategori', label: 'Kategori' },
        { key: 'urunAdi', label: 'Ürün Adı' },
        { key: 'aciklama', label: 'Açıklama', fullWidth: true },
        { key: 'miktar', label: 'Miktar', type: 'number' },
        { key: 'uygulamaMetrekare', label: 'Uygulama', type: 'number' },
        { key: 'toplamFiyat', label: 'Tutar', type: 'currency' },
        { key: 'durum', label: 'Durum', type: 'status' },
    ];

    const headerFilter = (
        <div className="flex items-center gap-3">
            <StokSekme activeTab={activeTab} onTabChange={setActiveTab} />
            {activeTab === 'kullanim' && (
                <select value={filterKategori} onChange={(e) => setFilterKategori(e.target.value)} className="px-3 py-2 bg-bg-primary border border-border-light rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/50 min-w-[160px]">
                    <option value="">Tüm Kategoriler</option>
                    {alciSivaKategorileri.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
                </select>
            )}
        </div>
    );

    return (
        <>
            <MaterialPageLayout title="Alçı / Sıva" subtitle="İnce İşler" icon={Square} iconBgColor="bg-neutral-500" backLink="/insaat/ince" stats={statsCards} onAddClick={activeTab === 'kullanim' ? handleAdd : undefined} headerFilter={headerFilter}>
                {activeTab === 'kullanim' ? (
                    <AlciSivaTable data={filteredData} onView={handleView} onEdit={handleEdit} onDelete={handleDelete} onUpdateStock={() => { }} />
                ) : (
                    <StokTablosu stokData={stokHavuzuData} hareketData={hareketlerData} hareketTipleri={hareketTipleri} birim="çuval" onAktarim={() => setIsAktarOpen(true)} />
                )}
            </MaterialPageLayout>
            <AlciSivaModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSubmit={handleSubmit} initialData={selectedItem} isLoading={isLoading} />
            <MaterialDetailModal isOpen={isDetailOpen} onClose={() => setIsDetailOpen(false)} data={selectedItem} fields={detailFields} title="Alçı/Sıva Detayı" />
            <StokAktarModal isOpen={isAktarOpen} onClose={() => setIsAktarOpen(false)} onSubmit={handleAktarim} malzemeTipi="alciSiva" stokData={stokHavuzuData} birim="çuval" isLoading={isLoading} />
            <ConfirmModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={handleConfirmDelete} title="Malzemeyi Sil" message={`"${selectedItem?.urunAdi}" malzemesini silmek istediğinize emin misiniz?`} isLoading={isLoading} />
            <OdemePlanMiniModal isOpen={isOdemePlanOpen} onClose={() => { setIsOdemePlanOpen(false); setOdemePlanData(null); }} onConfirm={handleOdemePlanConfirm} amount={odemePlanData?.amount} companyName={odemePlanData?.companyName} />
        </>
    );
}
